// buildScenarioDoc.js — assembles a map_meta/map_layer/map_marking/map_view ProseMirror-JSON
// document (the same shape `mapStateFromJSON` in schema/mapSchema.js expects) from a plain
// declarative description, for the map-editor demo scenarios under src/dev/mapDemo.
//
// This intentionally mirrors the real creation-time attrs shape used by useMapEditor.js's
// `creationIdentityAttrs` (author/createdAt/updatedAt/trackId/mobility/snapshots) so the
// resulting doc is indistinguishable from one a user actually drew in the editor.

import { newLayerId, newMarkingId, newTrackId, newMarkingSnapshot } from '../../components/map/schema/mapSchema';

/** One layer definition: `{ label, status?, visible? }`. Returns a stable layerId for markings to reference. */
export function makeLayer({ label, status = 'working', visible = true }) {
    return { layerId: newLayerId(), label, status, visible };
}

/**
 * One marking ("track") definition.
 *
 * @param {object} opts
 * @param {string} opts.layerId - id from `makeLayer`.
 * @param {'unit-icon'|'tactical-line'|'tactical-area'|'free-polygon'|'free-line'|'point'} opts.elementType
 * @param {'stationary'|'moving'|null} [opts.mobility]
 * @param {string} [opts.author] - display name attributed as author/updatedBy (demo-only, not a real uid).
 * @param {Array<[dtgMs:number, geometry:object, props:object]>} opts.snapshots - each tuple becomes
 *   one `newMarkingSnapshot(dtg, geometry, props)` entry, in the order given (should be ascending by dtg).
 * @param {string|null} [opts.graphicType]
 */
export function makeTrack({
    layerId, elementType, mobility = null, author = 'OSINT Demo Seed', snapshots, graphicType = null,
}) {
    const built = snapshots.map(([dtg, geometry, props]) => newMarkingSnapshot(dtg, geometry, props));
    const first = built[0];
    const last = built[built.length - 1];
    return {
        type: 'map_marking',
        attrs: {
            markingId: newMarkingId(),
            layerId,
            mode: elementType,
            elementType,
            author,
            createdAt: first.dtg,
            updatedAt: last.dtg,
            updatedBy: author,
            trackId: newTrackId(),
            mobility,
            snapshots: built,
            graphicType,
        },
    };
}

/**
 * Assemble the full ProseMirror-JSON doc.
 *
 * @param {object} opts
 * @param {number} opts.dtgStartMs - shared timeline range start (map_meta).
 * @param {number} opts.dtgEndMs - shared timeline range end (map_meta).
 * @param {Array} opts.layers - results of `makeLayer`.
 * @param {Array} opts.tracks - results of `makeTrack`.
 * @param {Array<{name:string, bbox:[number,number,number,number]}>} [opts.views] - saved crop views.
 */
export function buildScenarioDoc({ dtgStartMs, dtgEndMs, layers, tracks, views = [] }) {
    return {
        type: 'doc',
        content: [
            { type: 'map_meta', attrs: { dtgStartMs, dtgEndMs } },
            ...layers.map((l, i) => ({
                type: 'map_layer',
                attrs: { layerId: l.layerId, label: l.label, visible: l.visible, order: i, status: l.status, ownerId: null },
            })),
            ...tracks,
            ...views.map((v) => ({
                type: 'map_view',
                attrs: { viewId: `view-${v.name}`, name: v.name, bbox: v.bbox },
            })),
        ],
    };
}

/** `Date.UTC(...)` convenience so scenario files can write readable calendar dates. */
export function dtg(year, month, day, hour = 12, minute = 0) {
    return Date.UTC(year, month - 1, day, hour, minute);
}
